import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Tab,
  TabList,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { ApiError } from '../api/client';
import ConsentStatusChip from '../components/ConsentStatusChip';
import CopyValueButton from '../components/CopyValueButton';
import RelativeTime from '../components/RelativeTime';
import { useRefreshTenant, useTenantDetailQuery } from '../hooks/useTenantDetail';
import { CertificateStateBadge } from './tenant/TenantApplicationPage';
import { HeaderFact, PageHeader } from '../components/abPrimitives';
import { ACCENT, IDENTIFIER, STROKE } from '../theme/tokens';

// FD-284 (FD-ADR-031 §1) — tenant detail as TABS in the content column.
//
// This retires TenantSubRail, the SECOND of the three full-height rails that
// grew beside the app rail. Same treatment FD-281 gave MigrationSubRail and for
// the same reason: the tabs navigate the same child routes the sub-rail did, so
// tab state is the URL — deep-linkable, back/forward-aware, and the existing
// route tests keep meaning what they meant.
//
// The layout is no longer full-bleed. AppShell provides the padded content
// column for every route now, so the `min-h-[100dvh]` flex wrapper that existed
// only to sit flush against a rail goes with the rail.

const TABS = [
  { key: 'overview', label: 'Overview & consent' },
  // FD-530 — the FD-ADR-020 application AirBridge authenticates AS in this tenant,
  // beside the account an operator signs in as. Its own tab rather than a section
  // on Service accounts: those are two different credentials with two different
  // owners, and the tab that already carries a state machine about one of them is
  // the worst place to put the other.
  { key: 'application', label: 'Application' },
  { key: 'service-accounts', label: 'Service accounts' },
  { key: 'service-principals', label: 'Service principals' },
  { key: 'migrations', label: 'Migrations & waves' },
] as const;

const useStyles = makeStyles({
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    // FD-466 — the accent TEXT rung, from the token module rather than a
    // literal. Every one of these back links carried a hardcoded hex through
    // two accent retirements (sky-700, then Rubix orange) and the drift gate
    // caught them both times; taking the value from `ACCENT` is what stops a
    // third.
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
});

// Presentational role list for the header (source · destination), casing-safe.
function titleCaseRole(role: string): string {
  if (!role) return role;
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

export default function TenantLayout() {
  const styles = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const query = useTenantDetailQuery(tenantId);
  const refresh = useRefreshTenant(tenantId);

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Tenant not found</MessageBarTitle>
            No tenant with ID <span className="font-mono text-xs">{tenantId}</span> is in the
            registry.
          </MessageBarBody>
          <MessageBarActions>
            <Link to="/tenants" className={styles.backLink}>
              Back to tenants
            </Link>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  if (query.isLoading) return <DetailSkeleton />;

  if (query.isError || !query.data) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load tenant</MessageBarTitle>
            {errorMessage(query.error)}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="subtle"
              size="small"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  const tenant = query.data;
  const title = tenant.displayName?.trim() || tenant.tenantDomain?.trim() || 'Tenant';

  // Read from the URL, not from state — a deep link into /service-accounts must
  // open on that tab, and back/forward must move it.
  const activeTab =
    TABS.find((t) => location.pathname.endsWith(`/${t.key}`))?.key ?? 'overview';

  return (
    <div className="flex flex-col gap-6">
      <Link to="/tenants" className={styles.backLink}>
        <ArrowLeft size={14} strokeWidth={1.5} />
        All tenants
      </Link>

      <header>
        <PageHeader
          title={title}
          badge={<ConsentStatusChip status={tenant.consentStatus} />}
          actions={
            <Button
              appearance="secondary"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? 'Re-checking…' : 'Re-check'}
            </Button>
          }
          facts={
            <>
              {tenant.tenantDomain?.trim() && (
                <HeaderFact label="Domain">{tenant.tenantDomain}</HeaderFact>
              )}
              <HeaderFact label="Tenant ID">
                <span className="inline-flex items-center gap-1">
                  <span style={IDENTIFIER} title={tenant.tenantId}>
                    {tenant.tenantId}
                  </span>
                  <CopyValueButton value={tenant.tenantId} label="tenant ID" />
                </span>
              </HeaderFact>
              {tenant.roles.length > 0 && (
                <HeaderFact label="Roles">
                  <span className="inline-flex flex-wrap gap-1">
                    {tenant.roles.map((role) => (
                      <Badge key={role} appearance="tint" color="informative">
                        {titleCaseRole(role)}
                      </Badge>
                    ))}
                  </span>
                </HeaderFact>
              )}
              {/* FD-531 — the certificate state, on the HEADER so it is on every
                  tab rather than only on the one an operator would have to know
                  to open. Nothing rotates unattended (`FD-ADR-046` D1 refused
                  the standing permission that would let it), so a certificate
                  that nobody looks at is one that expires mid-wave and surfaces
                  as an indistinguishable token failure. An operator who has to
                  go looking will not.
                  Rendered only when an application is on file: "not configured"
                  is the Application tab's subject, at length, and a fourth
                  header chip saying it would crowd the row for every tenant
                  that has not been provisioned yet. */}
              {tenant.tenantAppCertificate.state !== 'not-configured' && (
                <HeaderFact label="Certificate">
                  <span className="inline-flex items-center gap-2">
                    <CertificateStateBadge state={tenant.tenantAppCertificate.state} />
                    {/* A failed LAST rotation rides alongside rather than
                        instead of the expiry state — they answer different
                        questions, and due-AND-failed is the combination that
                        matters most. */}
                    {tenant.tenantAppCertificate.rotationStatus === 'failed' && (
                      <span className="font-display text-xs text-amber-800">
                        last rotation failed
                      </span>
                    )}
                  </span>
                </HeaderFact>
              )}

              {/* The registry is a CACHE over an authoritative Graph
                  (FD-ADR-023 §3), so its freshness is part of the reading and
                  stays in the header rather than being tucked into a tab. A
                  cache that hides its age is a cache that gets believed. */}
              <HeaderFact label="Checked">
                <RelativeTime iso={tenant.refreshedUtc} emptyPlaceholder="never" />
              </HeaderFact>
            </>
          }
        />

        {refresh.isError && (
          <div className="mt-3 max-w-xl">
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Re-check failed</MessageBarTitle>
                {errorMessage(refresh.error)} The last known snapshot is still shown.
              </MessageBarBody>
            </MessageBar>
          </div>
        )}
      </header>

      <div style={{ borderBottom: `1px solid ${STROKE.base}` }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => navigate(`/tenants/${tenantId}/${data.value}`)}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key}>
              {t.label}
            </Tab>
          ))}
        </TabList>
      </div>

      <Outlet />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton>
        <SkeletonItem size={16} style={{ width: '120px' }} />
      </Skeleton>
      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '12px' }}>
        <Skeleton>
          <SkeletonItem size={32} style={{ width: '40%' }} />
        </Skeleton>
        <Skeleton>
          <SkeletonItem size={16} style={{ width: '70%' }} />
        </Skeleton>
      </div>
      <Skeleton>
        <SkeletonItem size={128} style={{ width: '100%' }} />
      </Skeleton>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
